
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import {
  DollarSign,
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  Calendar,
  Clock,
  UserPlus,
  Users,
} from "lucide-react";

// Sample data for charts
const salesData = [
  { date: "Jan", total: 4230, online: 2800, offline: 1430 },
  { date: "Feb", total: 5320, online: 3600, offline: 1720 },
  { date: "Mar", total: 6780, online: 4500, offline: 2280 },
  { date: "Apr", total: 6120, online: 3800, offline: 2320 },
  { date: "May", total: 5890, online: 3600, offline: 2290 },
  { date: "Jun", total: 7450, online: 5100, offline: 2350 },
  { date: "Jul", total: 8120, online: 5800, offline: 2320 },
];

const dailySales = [
  { time: "08:00", sales: 1200 },
  { time: "10:00", sales: 1800 },
  { time: "12:00", sales: 2400 },
  { time: "14:00", sales: 2100 },
  { time: "16:00", sales: 2800 },
  { time: "18:00", sales: 3200 },
  { time: "20:00", sales: 2600 },
];

const Sales = () => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[#333333]">Sales Overview</h1>
        <p className="text-muted-foreground">Monitor your sales performance across all channels</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-[#8CC84B]/20 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-[#4D9E41]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">RM 87,432.89</div>
            <p className="text-xs text-muted-foreground flex items-center">
              <span className="flex items-center text-[#4D9E41]">
                <TrendingUp className="mr-1 h-4 w-4" />
                +23.5%
              </span> 
              from last month
            </p>
          </CardContent>
        </Card>
        <Card className="border-[#8CC84B]/20 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Online Orders</CardTitle>
            <ShoppingCart className="h-4 w-4 text-[#4D9E41]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">3,452</div>
            <p className="text-xs text-muted-foreground flex items-center">
              <span className="flex items-center text-[#4D9E41]">
                <TrendingUp className="mr-1 h-4 w-4" />
                +12.8%
              </span> 
              from last month
            </p>
          </CardContent>
        </Card>
        <Card className="border-[#8CC84B]/20 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In-Store Sales</CardTitle>
            <Users className="h-4 w-4 text-[#4D9E41]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">2,854</div>
            <p className="text-xs text-muted-foreground flex items-center">
              <span className="flex items-center text-[#4D9E41]">
                <TrendingUp className="mr-1 h-4 w-4" />
                +5.2%
              </span> 
              from last month
            </p>
          </CardContent>
        </Card>
        <Card className="border-[#8CC84B]/20 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New Customers</CardTitle>
            <UserPlus className="h-4 w-4 text-[#4D9E41]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">738</div>
            <p className="text-xs text-muted-foreground flex items-center">
              <span className="flex items-center text-red-600">
                <TrendingDown className="mr-1 h-4 w-4" />
                -1.8%
              </span> 
              from last month
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="monthly" className="w-full">
        <TabsList className="bg-[#F2FCE2] border border-[#8CC84B]/20">
          <TabsTrigger value="monthly" className="data-[state=active]:bg-[#8CC84B] data-[state=active]:text-white">
            <Calendar className="w-4 h-4 mr-2" /> Monthly
          </TabsTrigger>
          <TabsTrigger value="daily" className="data-[state=active]:bg-[#8CC84B] data-[state=active]:text-white">
            <Clock className="w-4 h-4 mr-2" /> Daily
          </TabsTrigger>
        </TabsList>

        <TabsContent value="monthly" className="mt-6 space-y-6">
          <Card className="col-span-1 shadow-sm border-[#8CC84B]/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-medium flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-[#4D9E41]" />
                Monthly Sales Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={salesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5F5DA" />
                    <XAxis dataKey="date" stroke="#8CC84B" />
                    <YAxis stroke="#8CC84B" />
                    <Tooltip />
                    <Area type="monotone" dataKey="online" stackId="1" stroke="#4D9E41" fill="#4D9E41" fillOpacity={0.6} />
                    <Area type="monotone" dataKey="offline" stackId="1" stroke="#8CC84B" fill="#8CC84B" fillOpacity={0.6} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-4 mt-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-[#4D9E41] rounded-full mr-2" />
                  <span className="text-xs">Online Sales</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-[#8CC84B] rounded-full mr-2" />
                  <span className="text-xs">In-Store Sales</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="daily" className="mt-6 space-y-6">
          <Card className="col-span-1 shadow-sm border-[#8CC84B]/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-medium flex items-center">
                <Clock className="w-5 h-5 mr-2 text-[#4D9E41]" />
                Today's Sales by Hour
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailySales}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5F5DA" />
                    <XAxis dataKey="time" stroke="#8CC84B" />
                    <YAxis stroke="#8CC84B" />
                    <Tooltip />
                    <Bar dataKey="sales" fill="#4D9E41" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Sales;
